/**
 * Finanzen – sensibler Bereich.
 *
 * Vier Kennzahlen, Monatsverlauf, kumulierte Jahresansicht, Gewinnprognose und
 * die Ablage der Buchhaltungsberichte.
 *
 * Zur Beschriftung: alle Beträge sind ohne Umsatzsteuer. Der Gewinn ist aber
 * NICHT „vor Steuern" – er stammt aus der Saldenliste und enthält deren
 * Kontenklasse 8, also die bereits gebuchte Körperschaftsteuer-Vorauszahlung.
 * Der Prototyp schrieb an mehreren Stellen „vor Steuern"; das war für den
 * Auftragswert richtig (dort heißt es: ohne USt) und für den Gewinn falsch.
 */

import { useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../api';
import { useKora } from '../zustand';
import { IconSchloss } from '../komponenten/Icons';
import { betragLesen, dat, eurKurz, gruppiere } from '../hilfen/format';
import { Monatsdiagramm } from '../komponenten/Monatsdiagramm';
import type {
  BerichtAbgleich,
  BerichteAntwort,
  Finanzen as FinanzenDaten,
  SaldenAbgleich,
} from '../typen';
import './finanzen.css';

export function Finanzen() {
  const { toastZeigen } = useKora();
  const queryClient = useQueryClient();
  const dateiFeld = useRef<HTMLInputElement>(null);
  const [monat, setMonat] = useState('');
  const [uploadFehler, setUploadFehler] = useState('');
  const [monateOffen, setMonateOffen] = useState(false);
  /** Von/bis-Auswahl – klappt nur bei Bedarf auf. */
  const [zeitraumOffen, setZeitraumOffen] = useState(false);

  /**
   * Auswertungszeitraum. Leer heißt „wie der Server voreinstellt" – das ist
   * das Jahr des jüngsten Berichts, begrenzt auf die Monate, die es gibt.
   */
  const [zeitraum, setZeitraum] = useState<{ von: string; bis: string } | null>(null);

  const finanzen = useQuery({
    queryKey: ['finanzen', zeitraum?.von ?? '', zeitraum?.bis ?? ''],
    queryFn: () =>
      api.hole<FinanzenDaten>(
        zeitraum ? `/finanzen?von=${zeitraum.von}&bis=${zeitraum.bis}` : '/finanzen',
      ),
    // Beim Umschalten die alten Zahlen stehen lassen, statt die Seite leer
    // werden zu lassen – sonst springt das Layout bei jedem Klick.
    placeholderData: (vorher) => vorher,
  });

  const berichte = useQuery({
    queryKey: ['berichte'],
    queryFn: () => api.hole<BerichteAntwort>('/finanzen/berichte'),
  });

  const hochladen = useMutation({
    mutationFn: (datei: File) => {
      const formular = new FormData();
      formular.append('monat', monat || berichte.data?.monate[0]?.wert || '');
      formular.append('datei', datei);
      return api.datei<{
        id: number;
        ersetzt: boolean;
        pfad: string | null;
        hinweisSharePoint: boolean;
        fehler: string | null;
      }>('/finanzen/berichte', formular);
    },
    onSuccess: (antwort) => {
      void queryClient.invalidateQueries({ queryKey: ['berichte'] });
      if (antwort.hinweisSharePoint) {
        setUploadFehler(
          antwort.fehler
            ? `In KORA vermerkt, aber die Ablage in SharePoint hat nicht geklappt: ${antwort.fehler}`
            : 'In KORA vermerkt, aber die Ablage in SharePoint hat nicht geklappt – siehe Protokoll.',
        );
        return;
      }
      toastZeigen(
        antwort.ersetzt
          ? 'Bericht ersetzt – die vorherige Fassung dieses Monats wurde überschrieben.'
          : 'Buchhaltungsbericht abgelegt.',
      );
    },
    onError: (f) => setUploadFehler(f instanceof Error ? f.message : 'Hochladen fehlgeschlagen.'),
  });

  /**
   * Erst abgleichen, dann übernehmen. Der Abgleich schreibt nichts – er zeigt
   * nur, was passieren würde. Ein Knopf, der ungefragt Datensätze anlegt, ist
   * unangenehm.
   */
  const [abgleich, setAbgleich] = useState<BerichtAbgleich | null>(null);

  const pruefen = useMutation({
    mutationFn: () => api.hole<BerichtAbgleich>('/finanzen/berichte/abgleich'),
    onSuccess: (a) => {
      setUploadFehler('');
      setAbgleich(a);
    },
    onError: (f) => setUploadFehler(f instanceof Error ? f.message : 'Abgleich fehlgeschlagen.'),
  });

  /** Berichte übernehmen, die in SharePoint schon liegen. */
  const einlesen = useMutation({
    mutationFn: () =>
      api.sende<{ gefunden: number; neu: number; ergaenzt: number; jahre: string[] }>(
        '/finanzen/berichte/einlesen',
      ),
    onSuccess: (a) => {
      void queryClient.invalidateQueries({ queryKey: ['berichte'] });
      setUploadFehler('');
      setAbgleich(null);
      toastZeigen(
        a.neu || a.ergaenzt
          ? `${a.gefunden} Berichte gefunden · ${a.neu} neu, ${a.ergaenzt} verknüpft.`
          : `${a.gefunden} Berichte gefunden – alle waren schon da.`,
      );
    },
    onError: (f) => setUploadFehler(f instanceof Error ? f.message : 'Einlesen fehlgeschlagen.'),
  });

  const monatSpeichern = useMutation({
    mutationFn: ({
      monat: m,
      umsatz,
      ausgaben,
      stand,
      quelle,
    }: {
      monat: string;
      umsatz: number;
      ausgaben: number;
      stand?: string;
      quelle?: string;
    }) => api.ersetze(`/finanzen/monat/${m}`, { umsatz, ausgaben, stand, quelle }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['finanzen'] }),
    onError: (f) => toastZeigen(f instanceof Error ? f.message : 'Speichern fehlgeschlagen.'),
  });

  /**
   * Zahlen aus den Saldenlisten. Auch hier zwei Schritte: erst zeigen, was
   * herauskommt, dann übernehmen. Bei Geldbeträgen erst recht.
   */
  const [salden, setSalden] = useState<SaldenAbgleich | null>(null);

  const saldenPruefen = useMutation({
    mutationFn: () => api.hole<SaldenAbgleich>('/finanzen/salden/abgleich'),
    onSuccess: (a) => {
      setUploadFehler('');
      setSalden(a);
    },
    onError: (f) =>
      setUploadFehler(f instanceof Error ? f.message : 'Die Saldenlisten waren nicht lesbar.'),
  });

  const saldenUebernehmen = useMutation({
    mutationFn: () =>
      api.sende<{ uebernommen: number; uebersprungen: number }>('/finanzen/salden/uebernehmen'),
    onSuccess: (a) => {
      void queryClient.invalidateQueries({ queryKey: ['finanzen'] });
      setSalden(null);
      toastZeigen(
        `${a.uebernommen} Monate übernommen` +
          (a.uebersprungen ? `, ${a.uebersprungen} ohne auswertbare Saldenliste.` : '.'),
      );
    },
    onError: (f) => setUploadFehler(f instanceof Error ? f.message : 'Übernehmen fehlgeschlagen.'),
  });

  const notiz = useMutation({
    mutationFn: ({ id, text }: { id: number; text: string }) =>
      api.aendere(`/finanzen/berichte/${id}`, { notiz: text }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['berichte'] }),
  });

  const d = finanzen.data;
  const b = berichte.data;

  /** Zielpfad des gewählten Monats – vom Server gebildet, nicht hier. */
  const gewaehlterMonat = monat || b?.monate[0]?.wert || '';
  const zielPfad = b?.monate.find((m) => m.wert === gewaehlterMonat)?.pfad ?? '';

  /*
   * Berichte nach Jahr filtern.
   *
   * Achtzehn gleich aussehende Zeilen sind keine Information. Voreingestellt
   * ist das Jahr des gewaehlten Zeitraums; die uebrigen Jahre stehen als
   * Segmente daneben.
   */
  const berichtsJahre = ['alle', ...new Set((b?.berichte ?? []).map((x) => x.monat.slice(0, 4)))]
    .filter((j, i, a) => a.indexOf(j) === i)
    .sort((x, y) => (x === 'alle' ? 1 : y === 'alle' ? -1 : y.localeCompare(x)));

  const [berichtsJahr, setBerichtsJahr] = useState<string>('');
  const jahrAktiv = berichtsJahr || d?.jahr || berichtsJahre[0] || 'alle';

  const sichtbareBerichte = (b?.berichte ?? []).filter(
    (x) => jahrAktiv === 'alle' || x.monat.startsWith(jahrAktiv),
  );

  /** Gewinn je Monat – fuer die Berichtsliste, ohne zweiten Abruf. */
  const gewinnJeMonat = new Map<string, number>(
    (d?.monatsWerte ?? []).map((m) => [m.monat, m.umsatz - m.ausgaben]),
  );

  function gewinnText(monatSchluessel: string): string {
    const wert = gewinnJeMonat.get(monatSchluessel);
    if (wert === undefined) return '';
    return (wert >= 0 ? '+' : '') + eurKurz(wert);
  }

  /**
   * Monate, die in Kora fehlen, in SharePoint aber schon liegen.
   *
   * Das ist der dringende Fall: Die Datei ist da, nur noch nicht uebernommen.
   * Er entsteht erst, nachdem jemand „Mit SharePoint abgleichen" gedrueckt hat –
   * bei jedem Seitenaufruf in SharePoint nachzusehen waere ein Abruf zu viel.
   */
  const inSharepointGefunden = (abgleich?.neu ?? []).filter((m) =>
    (d?.datenstand.offeneMonate ?? []).some((o) => o.wert === m.monat),
  );

  return (
    <div className="finanzen">
      {/*
        Kopfzeile mit dem Zeitraum als Segmentleiste.

        Er stand vorher in einer eigenen Karte, die mehr Fläche belegte als die
        Zahlen darunter – obwohl er nur eine Einstellung ist. Hier oben ist er
        sichtbar, ohne die Seite zu beherrschen.
      */}
      <div className="fin-kopf">
        <div>
          <div className="eh-kicker">Kaufmännisches</div>
          <div className="eh-titel">Finanzen</div>
          <div className="fin-untertitel">
            Wie das Jahr läuft – aus den Buchhaltungsberichten, ohne Umsatzsteuer.
          </div>
        </div>

        {d && (
          <Zeitraumleiste
            daten={d}
            laeuft={finanzen.isFetching}
            offen={zeitraumOffen}
            onUmschalten={() => setZeitraumOffen((o) => !o)}
            onWaehlen={(von, bis) => setZeitraum({ von, bis })}
          />
        )}
      </div>

      {finanzen.isLoading && <div className="eh-karte">Zahlen werden geladen …</div>}

      {d && (
        <div className="fin-raster">
          <div className="fin-spalte">
            <div className="fin-karte">
              {/*
                Eine Kopfzeile statt vier gleich lauter Kacheln: Der Gewinn ist
                die Aussage, Umsatz und Ausgaben sind die Werte, aus denen er
                entsteht. Vorher standen dieselben drei Zahlen dreimal auf der
                Seite – als Kennzahlenreihe, im letzten Bericht und kumuliert.
              */}
              <div className="fin-aussage">
                <div>
                  <div className="fin-label">{d.zeitraum.label}</div>
                  <div className="fin-aussage-zeile">
                    <span
                      className={`fin-gewinn ${d.zusammenfassung.gewinnZahl < 0 ? 'minus' : ''}`}
                    >
                      {d.zusammenfassung.gewinn}
                    </span>
                    <span className="fin-aussage-text">Gewinn laut Buchhaltung</span>
                    {d.zusammenfassung.marge !== null && (
                      <span className="fin-marge">Marge {d.zusammenfassung.marge} %</span>
                    )}
                  </div>
                </div>

                <div className="fin-nebenwerte">
                  <div className="fin-nebenwert">
                    <span className="fin-nebenwert-label">Umsatz</span>
                    <span className="fin-nebenwert-zahl">{d.zusammenfassung.umsatz}</span>
                  </div>
                  <div className="fin-nebenwert">
                    <span className="fin-nebenwert-label">Ausgaben</span>
                    <span className="fin-nebenwert-zahl">{d.zusammenfassung.ausgaben}</span>
                  </div>
                </div>
              </div>

              <div className="fin-diagramm-kopf">
                <div className="fin-legende">
                  <span>
                    <span className="fin-legende-punkt" style={{ background: '#2A2320' }} />
                    Umsatz
                  </span>
                  <span>
                    <span className="fin-legende-punkt" style={{ background: '#CBBCAC' }} />
                    Ausgaben
                  </span>
                  <span>
                    <span className="fin-legende-punkt" style={{ background: 'var(--eh-rot)' }} />
                    Gewinn
                  </span>
                </div>
                <div className="fin-diagramm-hinweis">
                  Werte ohne Umsatzsteuer · Gewinn unter der Nulllinie = Verlust
                </div>
              </div>

              <Monatsdiagramm monate={d.monate} />
            </div>

            {/*
              Ergebnisse der Datenpflege.

              Die Knöpfe stehen rechts als ruhiges Werkzeug, ihre Ergebnisse
              hier: Sie sind breit, tabellarisch und wollen gelesen werden –
              in der schmalen Seitenspalte wären sie unbrauchbar.
            */}
            {salden && (
              <div className="fin-karte fin-arbeitsflaeche">
                <div className="fin-karte-kopf">
                  <div className="fin-label">Saldenlisten</div>
                  <button
                    type="button"
                    className="fin-textknopf"
                    onClick={() => setSalden(null)}
                    disabled={saldenUebernehmen.isPending}
                  >
                    Schließen
                  </button>
                </div>
                <div className="fin-hinweis">
                  Jeder Monat wurde gegen die Ergebniszeile seiner eigenen Saldenliste gerechnet.
                  Übernommen wird nur, was übereinstimmt.
                </div>

                <div className="fin-salden-tabelle">
                  <div className="fin-salden-zeile fin-salden-kopf">
                    <div>Monat</div>
                    <div>Umsatz</div>
                    <div>Ausgaben</div>
                    <div>Gewinn</div>
                    <div>Stand</div>
                    <div />
                  </div>
                  {salden.monate.map((m) => (
                    <div className="fin-salden-zeile" key={m.monat}>
                      <div className="fin-salden-monat">{m.label}</div>
                      {m.lesbar ? (
                        <>
                          <div className="fin-salden-zahl">{m.umsatzText}</div>
                          <div className="fin-salden-zahl">{m.ausgabenText}</div>
                          <div
                            className="fin-salden-zahl"
                            style={{
                              color: (m.gewinn ?? 0) < 0 ? 'var(--eh-rot)' : 'var(--eh-gruen)',
                            }}
                          >
                            {m.gewinnText}
                          </div>
                          <div className="fin-salden-stand">{m.stand ?? '—'}</div>
                          <div className={`fin-salden-marke ${m.aenderung}`}>{m.aenderung}</div>
                        </>
                      ) : (
                        <div className="fin-salden-hinweis">{m.hinweis}</div>
                      )}
                    </div>
                  ))}
                </div>

                {salden.jahre
                  .filter((j) => j.monate > 0)
                  .map((j) => (
                    <div className="fin-salden-jahr" key={j.jahr}>
                      <strong>{j.jahr}</strong>: Summe der {j.monate} Monate {j.summeMonate}, laut
                      jüngster Liste {j.kumuliert} seit Jahresbeginn.
                      {Math.abs(j.abweichendeCent) > 100 && (
                        <>
                          {' '}
                          Der Unterschied von {j.abweichung} kommt von Nachbuchungen in bereits
                          gedruckten Monaten – die kumulierte Zahl ist die aktuellere.
                        </>
                      )}
                    </div>
                  ))}

                <button
                  type="button"
                  className="fin-uebernehmen"
                  onClick={() => saldenUebernehmen.mutate()}
                  disabled={saldenUebernehmen.isPending || !salden.monate.some((m) => m.lesbar)}
                >
                  {saldenUebernehmen.isPending
                    ? 'Wird übernommen …'
                    : `${salden.monate.filter((m) => m.lesbar).length} Monate übernehmen`}
                </button>
              </div>
            )}

            {abgleich && (
              <div className="fin-karte fin-arbeitsflaeche">
                <div className="fin-karte-kopf">
                  <div className="fin-label">SharePoint-Abgleich</div>
                  <button
                    type="button"
                    className="fin-textknopf"
                    onClick={() => setAbgleich(null)}
                    disabled={einlesen.isPending}
                  >
                    Schließen
                  </button>
                </div>
                <div className="fin-hinweis">
                  In SharePoint gefunden: <strong>{abgleich.gefunden}</strong> Berichte
                  {abgleich.jahre.length ? ` (${abgleich.jahre.join(', ')})` : ''}
                </div>

                <ul className="fin-abgleich-liste">
                  <li>
                    <span className="fin-abgleich-zahl neu">{abgleich.neu.length}</span>
                    werden neu übernommen
                    {abgleich.neu.length > 0 && (
                      <span className="fin-abgleich-monate">
                        {abgleich.neu.map((m) => m.label).join(' · ')}
                      </span>
                    )}
                  </li>
                  <li>
                    <span className="fin-abgleich-zahl">{abgleich.verknuepfen.length}</span>
                    bekommen nur die Verknüpfung nachgetragen
                    {abgleich.verknuepfen.length > 0 && (
                      <span className="fin-abgleich-monate">
                        {abgleich.verknuepfen.map((m) => m.label).join(' · ')}
                      </span>
                    )}
                  </li>
                  <li>
                    <span className="fin-abgleich-zahl">{abgleich.unveraendert.length}</span>
                    sind bereits vollständig – bleiben unverändert
                  </li>
                  {abgleich.luecken.length > 0 && (
                    <li>
                      <span className="fin-abgleich-zahl fehlt">{abgleich.luecken.length}</span>
                      fehlen ganz – weder in SharePoint noch in KORA
                      <span className="fin-abgleich-monate">
                        {abgleich.luecken.map((m) => m.label).join(' · ')}
                      </span>
                    </li>
                  )}
                </ul>

                <button
                  type="button"
                  className="fin-uebernehmen"
                  onClick={() => einlesen.mutate()}
                  disabled={
                    einlesen.isPending || abgleich.neu.length + abgleich.verknuepfen.length === 0
                  }
                >
                  {einlesen.isPending
                    ? 'Wird übernommen …'
                    : abgleich.neu.length + abgleich.verknuepfen.length === 0
                      ? 'Nichts zu übernehmen'
                      : `${abgleich.neu.length + abgleich.verknuepfen.length} übernehmen`}
                </button>
              </div>
            )}

            {monateOffen && (
              <div className="fin-karte fin-arbeitsflaeche">
                <div className="fin-karte-kopf">
                  <div className="fin-label">Monatszahlen {d.jahr} · ohne Umsatzsteuer</div>
                  <button
                    type="button"
                    className="fin-textknopf"
                    onClick={() => setMonateOffen(false)}
                  >
                    Schließen
                  </button>
                </div>
                <div className="fin-hinweis">
                  Aus dem Buchhaltungsbericht übertragen. Leere Monate bleiben im Diagramm außen vor.
                </div>
                <div className="fin-monatsraster">
                  {d.monatsWerte.map((m) => (
                    <div className="fin-monatsfeld" key={m.monat}>
                      <span className="fin-monatsfeld-label">{m.label}</span>
                      <label>
                        <span>Umsatz</span>
                        <input
                          className="fin-monatsfeld-eingabe"
                          defaultValue={m.umsatz ? gruppiere(m.umsatz) : ''}
                          placeholder="0"
                          inputMode="numeric"
                          onBlur={(e) => {
                            const wert = betragLesen(e.target.value) ?? 0;
                            e.target.value = wert ? gruppiere(wert) : '';
                            if (wert !== m.umsatz) {
                              monatSpeichern.mutate({
                                monat: m.monat,
                                umsatz: wert,
                                ausgaben: m.ausgaben,
                              });
                            }
                          }}
                        />
                      </label>
                      <label>
                        <span>Ausgaben</span>
                        <input
                          className="fin-monatsfeld-eingabe"
                          defaultValue={m.ausgaben ? gruppiere(m.ausgaben) : ''}
                          placeholder="0"
                          inputMode="numeric"
                          onBlur={(e) => {
                            const wert = betragLesen(e.target.value) ?? 0;
                            e.target.value = wert ? gruppiere(wert) : '';
                            if (wert !== m.ausgaben) {
                              monatSpeichern.mutate({
                                monat: m.monat,
                                umsatz: m.umsatz,
                                ausgaben: wert,
                              });
                            }
                          }}
                        />
                      </label>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Kumulierter Verlauf – beantwortet die Richtung übers Jahr, nicht
                das Auf und Ab je Monat. Zwei Fragen, zwei Darstellungen. */}
            {d.verlauf.length > 1 && (
              <div className="fin-karte">
                <div className="fin-karte-kopf">
                  <div className="fin-label">Entwicklung {d.zeitraum.label} · aufsummiert</div>
                  <div className="fin-legende">
                    <span>
                      <span className="fin-legende-punkt" style={{ background: '#2A2320' }} />
                      Umsatz kumuliert
                    </span>
                    <span>
                      <span className="fin-legende-punkt" style={{ background: 'var(--eh-rot)' }} />
                      Gewinn kumuliert
                    </span>
                  </div>
                </div>
                <Verlaufskurve verlauf={d.verlauf} max={d.verlaufMax} />
              </div>
            )}

            {/* --- Buchhaltungsberichte --- */}
            <div className="fin-karte">
              <div className="fin-karte-kopf">
                <div>
                  <div className="fin-label">Buchhaltungsberichte</div>
                  <div className="fin-anzahl">
                    {b?.anzahlText ?? ''}
                    {d.datenstand.offeneMonate.length > 0 &&
                      ` · ${d.datenstand.offeneMonate.map((m) => m.label).join(', ')} fehlt`}
                  </div>
                </div>

                <div className="fin-kopf-rechts">
                  {/* Standardmäßig nur das gewählte Jahr – achtzehn gleich
                      aussehende Zeilen sind keine Information. */}
                  <div className="fin-jahrfilter">
                    {berichtsJahre.map((j) => (
                      <button
                        key={j}
                        type="button"
                        className={`fin-segment ${berichtsJahr === j ? 'aktiv' : ''}`}
                        onClick={() => setBerichtsJahr(j)}
                      >
                        {j === 'alle' ? 'Alle' : j}
                      </button>
                    ))}
                  </div>

                  <label className="fin-pdf-knopf">
                    + PDF hochladen
                    <input
                      ref={dateiFeld}
                      type="file"
                      accept="application/pdf"
                      style={{ display: 'none' }}
                      onChange={(e) => {
                        const datei = e.target.files?.[0];
                        setUploadFehler('');
                        if (datei) hochladen.mutate(datei);
                        e.target.value = '';
                      }}
                    />
                  </label>
                </div>
              </div>

              <div className="fin-upload-monat">
                <label>
                  <span>Berichtsmonat</span>
                  <select
                    className="fin-auswahl"
                    value={monat || b?.monate[0]?.wert || ''}
                    onChange={(e) => setMonat(e.target.value)}
                  >
                    {(b?.monate ?? []).map((o) => (
                      <option key={o.wert} value={o.wert}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                </label>
                <span className="fin-hinweis">
                  {hochladen.isPending
                    ? 'Wird hochgeladen …'
                    : zielPfad
                      ? `Wird abgelegt unter ${zielPfad}`
                      : 'Ein PDF pro Monat, vom Steuerberater'}
                </span>
              </div>

              {uploadFehler && <div className="eh-fehler">{uploadFehler}</div>}

              {/* Eine Zeile je Monat, Notiz direkt bearbeitbar, Rahmen erst
                  beim Überfahren – die Liste ist ein Register, kein Formular. */}
              <div className="fin-berichte">
                {sichtbareBerichte.map((bericht) => (
                  <div className="fin-bericht" key={bericht.id}>
                    <span className="fin-bericht-monat">{bericht.monatLabel}</span>
                    <span className={`fin-bericht-gewinn ${gewinnJeMonat.get(bericht.monat) ?? 0 < 0 ? 'minus' : ''}`}>
                      {gewinnText(bericht.monat)}
                    </span>
                    <input
                      className="fin-bericht-notiz"
                      defaultValue={bericht.notiz}
                      placeholder="Notiz zum Bericht …"
                      onBlur={(e) => {
                        if (e.target.value !== bericht.notiz) {
                          notiz.mutate({ id: bericht.id, text: e.target.value });
                        }
                      }}
                    />
                    {bericht.hatUrl ? (
                      <a
                        className="fin-bericht-pdf"
                        href={bericht.downloadUrl}
                        target="_blank"
                        rel="noreferrer"
                      >
                        PDF ↗
                      </a>
                    ) : (
                      <span className="fin-bericht-ohne" title="Datei liegt nicht in SharePoint">
                        nur Eintrag
                      </span>
                    )}
                  </div>
                ))}

                {sichtbareBerichte.length === 0 && (
                  <div className="fin-leer">Für diese Auswahl ist kein Bericht abgelegt.</div>
                )}
              </div>

              <div className="fin-berichte-fuss">
                <span>
                  {berichtsJahr === 'alle' || berichtsJahre.length <= 2
                    ? 'alle angezeigt'
                    : `${sichtbareBerichte.length} von ${b?.berichte.length ?? 0} angezeigt`}
                </span>
                {b?.buchhaltungAktiv && !abgleich && (
                  <button
                    type="button"
                    className="fin-textknopf"
                    onClick={() => pruefen.mutate()}
                    disabled={pruefen.isPending}
                    title="Sieht in SharePoint nach – schreibt noch nichts"
                  >
                    {pruefen.isPending ? 'Wird gesucht …' : 'Mit SharePoint abgleichen'}
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* ---- Rechte Spalte: Ist, Erwartung, Datenstand, Werkzeug ---- */}
          <div className="fin-seitenspalte">
            <div className="fin-karte fin-ist">
              <div className="fin-label">
                <span className="fin-marker voll" /> Ist · gebucht
              </div>
              <div className={`fin-ist-zahl ${d.zusammenfassung.gewinnZahl < 0 ? 'minus' : ''}`}>
                {d.zusammenfassung.gewinn}
              </div>
              <div className="fin-ist-text">{d.zeitraum.label}</div>
            </div>

            {/*
              Ist und Erwartung sind visuell getrennt, nicht nur farblich:
              gebuchte Zahlen deckend, erwartete schraffiert und gestrichelt.
              Wer die Farben nicht unterscheiden kann, sieht den Unterschied
              trotzdem – und niemand hält eine Schätzung für eine Buchung.
            */}
            {d.prognose.gilt ? (
              <div className="fin-karte fin-erwartet">
                <div className="fin-label">
                  <span className="fin-marker gestrichelt" /> Erwartet · noch nicht gebucht
                </div>
                <div className="fin-erwartet-zahl">+ {d.prognose.beitrag}</div>
                <div className="fin-ist-text">
                  Deckungsbeitrag aus {d.prognose.anzahl}
                </div>

                <div className="fin-prognose-zeile">
                  <span>Jahresprognose</span>
                  <span className="fin-prognose-summe">{d.prognose.summe}</span>
                </div>
                <div className="fin-prognose-schiene">
                  <div className="fin-schiene-ist" style={{ width: d.prognose.istBreite }} />
                  <div className="fin-schiene-offen" style={{ width: d.prognose.offenBreite }} />
                </div>
                <div className="fin-hinweis">
                  Erwartete Beträge sind schraffiert dargestellt – sie stehen noch in keinem
                  Buchhaltungsbericht und können sich ändern.
                </div>

                {/*
                  Woher die erwartete Zahl kommt. Ohne diese Aufschlüsselung
                  ist die Prognose eine Zahl, die man glauben muss.
                */}
                {d.prognose.zeilen.length > 0 && (
                  <details className="fin-herkunft">
                    <summary>Woraus sich das ergibt</summary>
                    {d.prognose.zeilen.map((z) => (
                      <div className="fin-herkunft-zeile" key={z.prefix}>
                        <span className="fin-herkunft-name">
                          {z.prefix} · {z.auftragswert}
                        </span>
                        <span className="fin-herkunft-marge">
                          {z.ohneAufschlag
                            ? 'kein Aufschlag hinterlegt'
                            : `${z.aufschlag} % → ${z.margeText}`}
                        </span>
                        <span className="fin-herkunft-wert">{z.beitrag}</span>
                      </div>
                    ))}
                    <div className="fin-hinweis">
                      Aus {d.prognose.offen} Auftragsvolumen · {d.prognose.anzahl}
                    </div>
                  </details>
                )}
              </div>
            ) : (
              <div className="fin-karte fin-erwartet">
                <div className="fin-label">
                  <span className="fin-marker gestrichelt" /> Erwartet
                </div>
                <div className="fin-hinweis">
                  Für einen abgeschlossenen Zeitraum gibt es keine Prognose. Die offenen Aufträge
                  von heute gehören nicht zu {d.zeitraum.label}.
                </div>
              </div>
            )}

            {/*
              Datenstand: Die Seite prüft selbst, ob ein Bericht aussteht.
              Der Steuerberater legt unregelmäßig ab – niemand soll im Kopf
              behalten müssen, welcher Monat schon da sein müsste.
            */}
            <div
              className={`fin-karte fin-datenstand ${
                inSharepointGefunden.length > 0
                  ? 'rot'
                  : d.datenstand.offeneMonate.length > 0
                    ? 'gelb'
                    : ''
              }`}
            >
              <div className="fin-karte-kopf">
                <div className="fin-label">Datenstand</div>
                {d.datenstand.offeneMonate.length > 0 && (
                  <span className="fin-stand-marke">
                    {d.datenstand.offeneMonate.length === 1
                      ? '1 Monat offen'
                      : `${d.datenstand.offeneMonate.length} Monate offen`}
                  </span>
                )}
              </div>

              <div className="fin-stand-text">
                {d.datenstand.letzterMonat
                  ? `Letzter Bericht: ${d.datenstand.letzterMonatLabel}${
                      d.datenstand.eingelesenAm
                        ? `, eingelesen am ${dat(d.datenstand.eingelesenAm)}`
                        : ''
                    }.`
                  : 'Noch kein Bericht erfasst.'}
              </div>

              {inSharepointGefunden.length > 0 && (
                <div className="fin-stand-warnung">
                  <div>
                    Der Bericht für {inSharepointGefunden[0]!.label} liegt bereits in SharePoint – in KORA
                    fehlt der Monat noch.
                  </div>
                  <button
                    type="button"
                    className="fin-stand-knopf"
                    onClick={() => einlesen.mutate()}
                    disabled={einlesen.isPending}
                  >
                    {einlesen.isPending
                      ? 'Wird übernommen …'
                      : `${inSharepointGefunden[0]!.label} übernehmen`}
                  </button>
                  <button
                    type="button"
                    className="fin-textknopf"
                    onClick={() => pruefen.mutate()}
                    disabled={pruefen.isPending}
                  >
                    In SharePoint nachsehen
                  </button>
                </div>
              )}

              {inSharepointGefunden.length === 0 && d.datenstand.offeneMonate.length > 0 && (
                <div className="fin-stand-warnung">
                  <div>
                    {d.datenstand.offeneMonate.map((m) => m.label).join(', ')} fehlt noch – weder in
                    KORA noch in SharePoint gefunden.
                  </div>
                </div>
              )}
            </div>

            {/*
              Datenpflege ist Werkzeug, nicht Inhalt: gesammelt hier als ruhige
              Zweitknöpfe, nicht als rote Schaltflächen quer über der Grafik.
            */}
            <div className="fin-werkzeug">
              {b?.buchhaltungAktiv && !salden && (
                <button
                  type="button"
                  className="fin-werkzeugknopf"
                  onClick={() => saldenPruefen.mutate()}
                  disabled={saldenPruefen.isPending}
                  title="Liest die Saldenlisten aus SharePoint und rechnet gegen deren eigene Ergebniszeile"
                >
                  {saldenPruefen.isPending
                    ? 'Saldenlisten werden gelesen …'
                    : 'Zahlen aus Saldenlisten holen'}
                </button>
              )}
              <button
                type="button"
                className="fin-werkzeugknopf"
                onClick={() => setMonateOffen((o) => !o)}
              >
                {monateOffen ? 'Zahlenpflege schließen' : 'Zahlen manuell pflegen'}
              </button>
            </div>

            <div className="fin-zugriff">
              <span className="fin-zugriff-icon">
                <IconSchloss />
              </span>
              <span>
                Sichtbar für Geschäftsführung und Kaufmännisches. Freigabe im Admin Center.
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
/**
 * Kumulierter Jahresverlauf als Kurve.
 *
 * Bewusst als SVG von Hand statt mit einer Diagramm-Bibliothek: es sind zwei
 * Linien auf einer gemeinsamen Skala, und eine Abhängigkeit von 200 kB
 * Fremdcode wäre dafür unverhältnismäßig. Die Skala richtet sich am kumulierten
 * Umsatz aus; der Gewinn liegt darunter und wird dadurch als Anteil lesbar.
 */
function Verlaufskurve({
  verlauf,
  max,
}: {
  verlauf: FinanzenDaten['verlauf'];
  max: number;
}) {
  const B = 800;
  const H = 220;
  const rand = { oben: 14, unten: 30, links: 8, rechts: 8 };
  const zeichenH = H - rand.oben - rand.unten;
  const zeichenB = B - rand.links - rand.rechts;

  // Auch negative Gewinne müssen Platz haben – sonst verschwindet die Kurve.
  const tief = Math.min(0, ...verlauf.map((v) => v.gewinn));
  const spanne = Math.max(1, max - tief);

  const x = (i: number) =>
    rand.links + (verlauf.length === 1 ? zeichenB / 2 : (i / (verlauf.length - 1)) * zeichenB);
  const y = (wert: number) => rand.oben + zeichenH - ((wert - tief) / spanne) * zeichenH;

  const pfad = (feld: 'umsatz' | 'gewinn') =>
    verlauf.map((v, i) => `${i === 0 ? 'M' : 'L'} ${x(i).toFixed(1)} ${y(v[feld]).toFixed(1)}`).join(' ');

  const nullLinie = tief < 0 ? y(0) : null;

  return (
    <div className="fin-kurve">
      <svg viewBox={`0 0 ${B} ${H}`} preserveAspectRatio="none" role="img"
        aria-label={`Kumulierter Umsatz und Gewinn, ${verlauf[0]?.label} bis ${verlauf[verlauf.length - 1]?.label}`}>
        {nullLinie !== null && (
          <line x1={rand.links} x2={B - rand.rechts} y1={nullLinie} y2={nullLinie}
            stroke="var(--eh-rahmen)" strokeWidth="1" strokeDasharray="4 4" />
        )}
        <path d={pfad('umsatz')} fill="none" stroke="#2A2320" strokeWidth="2.5"
          strokeLinejoin="round" strokeLinecap="round" vectorEffect="non-scaling-stroke" />
        <path d={pfad('gewinn')} fill="none" stroke="var(--eh-rot)" strokeWidth="2.5"
          strokeLinejoin="round" strokeLinecap="round" vectorEffect="non-scaling-stroke" />
        {verlauf.map((v, i) => (
          <g key={v.monat}>
            <circle cx={x(i)} cy={y(v.umsatz)} r="3.5" fill="#2A2320" />
            <circle cx={x(i)} cy={y(v.gewinn)} r="3.5" fill="var(--eh-rot)" />
            <title>{`${v.label}: Umsatz ${v.umsatzText} · Gewinn ${v.gewinnText}`}</title>
          </g>
        ))}
      </svg>
      <div className="fin-kurve-achse">
        {verlauf.map((v) => (
          <span key={v.monat}>{v.label}</span>
        ))}
      </div>
      <div className="fin-kurve-fuss">
        Stand {verlauf[verlauf.length - 1]?.label}: Umsatz{' '}
        <strong>{verlauf[verlauf.length - 1]?.umsatzText}</strong>, Gewinn{' '}
        <strong>{verlauf[verlauf.length - 1]?.gewinnText}</strong> – jeweils seit Jahresbeginn.
      </div>
    </div>
  );
}

/**
 * Auswertungszeitraum als kompakte Segmentleiste im Seitenkopf.
 *
 * Er stand vorher in einer eigenen Karte, die mehr Fläche belegte als die
 * Zahlen darunter – für eine Einstellung, die man selten ändert. Hier ist er
 * sichtbar, ohne die Seite zu beherrschen.
 *
 * Zur Auswahl stehen nur Monate, für die ein Bericht erfasst ist. Ein Jahr,
 * das erst im Juli beginnt, heißt deshalb auch „2025 · Jul–Dez" – sonst sieht
 * ein halbes Jahr fehlender Daten aus wie ein Einbruch.
 */
function Zeitraumleiste({
  daten,
  laeuft,
  offen,
  onUmschalten,
  onWaehlen,
}: {
  daten: FinanzenDaten;
  laeuft: boolean;
  offen: boolean;
  onUmschalten: () => void;
  onWaehlen: (von: string, bis: string) => void;
}) {
  const { zeitraum, verfuegbar } = daten;
  const alle = verfuegbar.monate;

  if (alle.length === 0) {
    return (
      <div className="fin-zeitraum-leer">
        Noch kein Buchhaltungsbericht erfasst – sobald einer eingelesen ist, lässt sich hier der
        Zeitraum wählen.
      </div>
    );
  }

  /*
   * Drei Segmente reichen: das laufende Jahr, der letzte Monat und – wenn es
   * mehrere Jahre gibt – das jüngste davon. Alles Weitere steckt hinter
   * „Zeitraum wählen"; eine Leiste mit acht Knöpfen wäre wieder eine Karte.
   */
  const segmente: { schluessel: string; label: string; von: string; bis: string }[] = [];
  const jahre = verfuegbar.jahre;
  const jetzigesJahr = jahre[jahre.length - 1];
  if (jetzigesJahr) {
    segmente.push({
      schluessel: jetzigesJahr.jahr,
      label: jetzigesJahr.label,
      von: jetzigesJahr.von,
      bis: jetzigesJahr.bis,
    });
  }
  if (alle.length > 12) {
    segmente.push({
      schluessel: '12m',
      label: 'Laufendes Jahr',
      von: alle[alle.length - 12]!.wert,
      bis: verfuegbar.letzter,
    });
  }
  segmente.push({
    schluessel: 'letzter',
    label: 'Letzter Monat',
    von: verfuegbar.letzter,
    bis: verfuegbar.letzter,
  });

  return (
    <div className="fin-zeitraum">
      <div className="fin-segmentleiste">
        {segmente.map((v) => {
          const aktiv = zeitraum.von === v.von && zeitraum.bis === v.bis;
          return (
            <button
              key={v.schluessel}
              type="button"
              className={`fin-segment ${aktiv ? 'aktiv' : ''}`}
              onClick={() => onWaehlen(v.von, v.bis)}
            >
              {v.label}
            </button>
          );
        })}
      </div>

      <button
        type="button"
        className={`fin-zeitraum-knopf ${offen ? 'offen' : ''}`}
        onClick={onUmschalten}
        aria-expanded={offen}
      >
        Zeitraum wählen
        <span className="fin-zeitraum-pfeil">{offen ? '▴' : '▾'}</span>
      </button>

      {offen && (
        <div className="fin-zeitraum-genau">
          <label>
            <span>von</span>
            <select value={zeitraum.von} onChange={(e) => onWaehlen(e.target.value, zeitraum.bis)}>
              {alle.map((m) => (
                <option key={m.wert} value={m.wert}>
                  {m.label}
                </option>
              ))}
            </select>
          </label>
          <label>
            <span>bis</span>
            <select value={zeitraum.bis} onChange={(e) => onWaehlen(zeitraum.von, e.target.value)}>
              {alle.map((m) => (
                <option key={m.wert} value={m.wert}>
                  {m.label}
                </option>
              ))}
            </select>
          </label>

          {/*
            Wie viele Berichte tatsächlich im Zeitraum liegen. „Juli bis Juni"
            klingt nach zwölf Monaten – ob es zwölf Berichte sind, steht hier.
          */}
          <div className="fin-zeitraum-bilanz">
            {zeitraum.mitBericht === zeitraum.monate
              ? `${zeitraum.monate === 1 ? '1 Bericht' : `${zeitraum.monate} Berichte`} · lückenlos`
              : `${zeitraum.mitBericht} von ${zeitraum.monate} Monaten erfasst${
                  zeitraum.luecken.length ? ` · es fehlt ${zeitraum.luecken.join(', ')}` : ''
                }`}
          </div>
        </div>
      )}

      {laeuft && <span className="fin-zeitraum-laeuft">wird berechnet …</span>}
    </div>
  );
}
